import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DownloadIcon from "@mui/icons-material/Download";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import FindInPageOutlinedIcon from "@mui/icons-material/FindInPageOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import documentoDocenteService from "../services/documentoDocenteService";
import VisorPdf from "./VisorPdf";

const NAVY = "#031A7F";
const TEAL = "#00C7DB";

/* =========================
   Origen de cada momento
========================= */
const ORIGENES = {
  docente: { label: "Del docente", color: "#15803d", fondo: "#dcfce7" },
  ampliado: { label: "Ampliado por el sistema", color: "#b45309", fondo: "#fef3c7" },
  sistema: { label: "Agregado por el sistema", color: "#4338ca", fondo: "#e0e7ff" },
};

const pastilla = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  px: 1.25,
  py: 0.4,
  borderRadius: 5,
  bgcolor: "rgba(255,255,255,0.12)",
  fontSize: 12.5,
  cursor: "default",
};

// Las comparativas anteriores guardaban los aportes del docente como texto
const normalizarAporte = (item) =>
  typeof item === "string"
    ? { texto: item, pagina: null, rects: [], legado: true }
    : { ...item, rects: item.rects ?? [] };

const ChipOrigen = ({ origen }) => {
  const o = ORIGENES[origen] ?? ORIGENES.sistema;
  return (
    <Chip
      size="small"
      label={o.label}
      sx={{ bgcolor: o.fondo, color: o.color, fontWeight: 700, border: `1px solid ${o.color}33` }}
    />
  );
};

const EncabezadoColumna = ({ icono, titulo, color }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1, color }}>
    {icono}
    <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase" }}>
      {titulo}
    </Typography>
  </Box>
);

const AporteDocente = ({ aporte, seleccionado, onSeleccionar }) => {
  const ubicable = aporte.pagina && aporte.rects.length > 0;

  const contenido = (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", width: "100%" }}>
      <Box sx={{ mt: "7px", width: 6, height: 6, borderRadius: "50%", bgcolor: "#15803d", flexShrink: 0 }} />
      <Typography variant="body2" sx={{ flex: 1, textAlign: "left", lineHeight: 1.5 }}>
        {aporte.texto}
      </Typography>
      {ubicable && (
        <Chip
          size="small"
          icon={<FindInPageOutlinedIcon sx={{ fontSize: 14 }} />}
          label={`pág. ${aporte.pagina}`}
          sx={{
            height: 22,
            fontSize: 11,
            fontWeight: 700,
            bgcolor: seleccionado ? NAVY : "#eef2ff",
            color: seleccionado ? "#fff" : NAVY,
            "& .MuiChip-icon": { color: "inherit" },
            flexShrink: 0,
          }}
        />
      )}
    </Box>
  );

  if (!ubicable) {
    return (
      <Tooltip
        title={aporte.legado ? "" : "No se encontró este fragmento en el texto del PDF"}
        placement="left"
      >
        <Box sx={{ px: 1, py: 0.75 }}>{contenido}</Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={aporte.cita ? `«${aporte.cita}»` : ""} placement="left">
      <ButtonBase
        onClick={onSeleccionar}
        sx={{
          width: "100%",
          px: 1,
          py: 0.75,
          borderRadius: 1.5,
          border: "1px solid",
          borderColor: seleccionado ? "#f59e0b" : "transparent",
          bgcolor: seleccionado ? "#fffbeb" : "transparent",
          transition: "background-color .15s, border-color .15s",
          "&:hover": { bgcolor: seleccionado ? "#fffbeb" : "#f1f5f9", borderColor: seleccionado ? "#f59e0b" : "#cbd5e1" },
        }}
      >
        {contenido}
      </ButtonBase>
    </Tooltip>
  );
};

/**
 * Comparativa por momento pedagógico entre el documento del docente
 * y la sesión generada. Al hacer clic en un aporte del docente se
 * resalta su ubicación en el documento original.
 */
const ComparativaDocentePanel = ({ sesionId, nombreSesion, onClose, mostrarNombre = true }) => {
  const [loading, setLoading] = useState(true);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState("");
  const [generando, setGenerando] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [resaltado, setResaltado] = useState(null);

  // En pantallas angostas los aportes y el documento se muestran por pestañas
  const esMovil = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [vista, setVista] = useState("aportes");

  // El PDF se carga una vez; no debe recargarse al generar la comparativa
  const hayDocumento = Boolean(datos);

  /* =========================
     Cargar comparativa
  ========================= */
  useEffect(() => {
    if (!sesionId) return;
    let isCancelled = false;

    setLoading(true);
    setError("");

    documentoDocenteService
      .obtenerDocumentoDeSesion(sesionId)
      .then((data) => {
        if (!isCancelled) setDatos(data);
      })
      .catch(() => {
        if (!isCancelled) setError("No se pudo cargar la comparativa.");
      })
      .finally(() => {
        if (!isCancelled) setLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [sesionId]);

  /* =========================
     Cargar documento original
  ========================= */
  useEffect(() => {
    if (!sesionId || !hayDocumento) return;
    let isCancelled = false;

    documentoDocenteService
      .obtenerPdfDocumentoDeSesion(sesionId)
      .then((blob) => {
        if (!isCancelled) setPdfBlob(new Blob([blob], { type: "application/pdf" }));
      })
      .catch(() => {
        if (!isCancelled) setError("No se pudo cargar el documento original.");
      });

    return () => {
      isCancelled = true;
    };
  }, [sesionId, hayDocumento]);

  const comparativa = datos?.comparativa;

  const momentos = useMemo(
    () =>
      (comparativa?.momentos ?? []).map((m) => ({
        ...m,
        docente: (m.docente ?? []).map(normalizarAporte),
      })),
    [comparativa]
  );

  // Total de viñetas de cada columna
  const aportes = useMemo(
    () => ({
      docente: momentos.reduce((n, m) => n + m.docente.length, 0),
      sistema: momentos.reduce((n, m) => n + (m.sistema?.length ?? 0), 0),
    }),
    [momentos]
  );

  const esLegado = momentos.some((m) => m.docente.some((a) => a.legado));

  // Solo genera la comparativa si falta (por ejemplo, si falló al generar la sesión)
  const handleGenerar = useCallback(async () => {
    setGenerando(true);
    setError("");
    setResaltado(null);

    try {
      const nueva = await documentoDocenteService.generarComparativa(sesionId);
      setDatos((prev) => ({ ...prev, comparativa: nueva }));
    } catch {
      setError("No se pudo generar la comparativa. Inténtalo nuevamente.");
    } finally {
      setGenerando(false);
    }
  }, [sesionId]);

  const handleDescargar = useCallback(async () => {
    setDescargando(true);

    try {
      await documentoDocenteService.descargarComparativaPdf(sesionId, nombreSesion);
    } catch {
      setError("No se pudo descargar la comparativa.");
    } finally {
      setDescargando(false);
    }
  }, [sesionId, nombreSesion]);

  /* =========================
     Encabezado
  ========================= */
  const encabezado = (
    <Box
      sx={{
        px: { xs: 1.5, md: 3 },
        py: { xs: 1.25, md: 2 },
        background: `linear-gradient(120deg, ${NAVY} 0%, #17339a 60%, #0e7490 100%)`,
        color: "#fff",
      }}
    >
      <Box sx={{ display: "flex", alignItems: { xs: "center", md: "flex-start" }, gap: { xs: 1, md: 1.5 } }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.14)",
            display: { xs: "none", md: "grid" },
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <CompareArrowsIcon sx={{ color: TEAL }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {mostrarNombre ? (
            <>
              <Typography variant="overline" sx={{ color: TEAL, fontWeight: 800, lineHeight: 1.4, letterSpacing: 1 }}>
                Comparativa con el documento del docente
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, fontSize: { xs: 16, md: 20 } }} noWrap>
                {nombreSesion || "Sesión de aprendizaje"}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, fontSize: { xs: 16, md: 20 } }}>
                Comparativa con el documento del docente
              </Typography>
              <Typography variant="body2" sx={{ color: "#cbd5e1", display: { xs: "none", md: "block" } }}>
                Qué traía el docente y qué agregó el sistema en cada parte de la sesión
              </Typography>
            </>
          )}
        </Box>

        {comparativa &&
          (esMovil ? (
            <Tooltip title="Descargar PDF">
              <span>
                <IconButton
                  onClick={handleDescargar}
                  disabled={descargando}
                  sx={{ bgcolor: TEAL, color: "#0F172A", "&:hover": { bgcolor: "#00a9b9" }, flexShrink: 0 }}
                >
                  {descargando ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Button
              size="small"
              variant="contained"
              onClick={handleDescargar}
              disabled={descargando}
              startIcon={descargando ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
              sx={{ bgcolor: TEAL, color: "#0F172A", fontWeight: 700, flexShrink: 0, "&:hover": { bgcolor: "#00a9b9" } }}
            >
              Descargar PDF
            </Button>
          ))}

        {onClose && (
          <IconButton onClick={onClose} sx={{ color: "#fff", ml: 0.5, mt: { md: -0.5 } }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {comparativa && (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mt: { xs: 1, md: 1.5 } }}>
          <Typography component="span" sx={{ display: { xs: "none", sm: "inline" }, fontSize: 12, fontWeight: 800, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 0.6 }}>
            Aportes
          </Typography>
          <Tooltip title="Viñetas de «Lo que traía el docente»">
            <Box sx={pastilla}>
              <PersonOutlineIcon sx={{ fontSize: 15, color: "#86efac" }} />
              <b>{aportes.docente}</b> del docente
            </Box>
          </Tooltip>
          <Tooltip title="Viñetas de «Lo que agregó el sistema»">
            <Box sx={pastilla}>
              <AutoAwesomeIcon sx={{ fontSize: 15, color: "#a5b4fc" }} />
              <b>{aportes.sistema}</b> del sistema
            </Box>
          </Tooltip>
        </Box>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {encabezado}
        <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!datos) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {encabezado}
        <Alert severity="info" sx={{ m: 2 }}>
          {error || "Esta sesión no se generó a partir de un documento del docente."}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, bgcolor: "#f8fafc" }}>
      {encabezado}

      {esMovil && (
        <Tabs
          value={vista}
          onChange={(_, valor) => setVista(valor)}
          variant="fullWidth"
          sx={{
            minHeight: 44,
            bgcolor: "#fff",
            borderBottom: "1px solid #e2e8f0",
            "& .MuiTab-root": { minHeight: 44, textTransform: "none", fontWeight: 700, color: "#475569" },
            "& .Mui-selected": { color: `${NAVY} !important` },
            "& .MuiTabs-indicator": { height: 3, borderRadius: 3, bgcolor: TEAL },
          }}
        >
          <Tab value="aportes" icon={<CompareArrowsIcon fontSize="small" />} iconPosition="start" label="Aportes" />
          <Tab value="documento" icon={<DescriptionOutlinedIcon fontSize="small" />} iconPosition="start" label="Documento" />
        </Tabs>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          gap: 2,
          p: { xs: 1, md: 2 },
          overflow: "hidden",
        }}
      >
        {/* Comparativa por momento pedagógico */}
        <Box
          sx={{
            flex: { xs: 1, md: "0 0 54%" },
            display: esMovil && vista !== "aportes" ? "none" : "block",
            overflow: "auto",
            minWidth: 0,
            pr: { md: 0.5 },
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!comparativa && (
            <Alert
              severity="info"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleGenerar}
                  disabled={generando}
                  startIcon={generando ? <CircularProgress size={14} color="inherit" /> : null}
                >
                  {generando ? "Generando…" : "Generar comparativa"}
                </Button>
              }
            >
              La comparativa con el documento del docente todavía no se ha generado.
            </Alert>
          )}

          {comparativa && (
            <>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  bgcolor: "#ecfeff",
                  border: "1px solid #a5f3fc",
                }}
              >
                <AutoAwesomeIcon sx={{ color: "#0e7490", mt: 0.25 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#155e75", mb: 0.5 }}>
                    Resumen
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#164e63", lineHeight: 1.6 }}>
                    {comparativa.resumen}
                  </Typography>
                </Box>
              </Box>

              {esLegado ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Esta comparativa es anterior a las referencias al documento, por eso sus aportes
                  no se pueden ubicar en el PDF.
                </Alert>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, color: "#475569" }}>
                  <TouchAppOutlinedIcon fontSize="small" />
                  <Typography variant="caption">
                    {esMovil
                      ? "Toca un aporte del docente para verlo resaltado en el documento original."
                      : "Haz clic en un aporte del docente para resaltarlo en el documento original."}
                  </Typography>
                </Box>
              )}

              {momentos.map((momento, indice) => {
                const origen = ORIGENES[momento.origen] ?? ORIGENES.sistema;
                return (
                  <Box
                    key={momento.momento}
                    sx={{
                      mb: 1.5,
                      bgcolor: "#fff",
                      borderRadius: 2,
                      border: "1px solid #e2e8f0",
                      borderLeft: `5px solid ${origen.color}`,
                      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
                      overflow: "hidden",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.25, px: { xs: 1.5, md: 2 }, py: 1.25, borderBottom: "1px solid #f1f5f9" }}>
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          bgcolor: NAVY,
                          color: "#fff",
                          display: "grid",
                          placeItems: "center",
                          fontSize: 13,
                          fontWeight: 800,
                          flexShrink: 0,
                        }}
                      >
                        {indice + 1}
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0F172A", flex: 1 }}>
                        {momento.titulo}
                      </Typography>
                      <ChipOrigen origen={momento.origen} />
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" } }}>
                      <Box sx={{ flex: 1, minWidth: 0, p: 1.5 }}>
                        <EncabezadoColumna
                          icono={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
                          titulo="Lo que traía el docente"
                          color="#15803d"
                        />
                        {momento.docente.length ? (
                          momento.docente.map((aporte, i) => {
                            const clave = `${momento.momento}-${i}`;
                            return (
                              <AporteDocente
                                key={clave}
                                aporte={aporte}
                                seleccionado={resaltado?.id === clave}
                                onSeleccionar={() => {
                                  setResaltado({
                                    id: clave,
                                    key: Date.now(),
                                    pagina: aporte.pagina,
                                    rects: aporte.rects,
                                  });
                                  if (esMovil) setVista("documento");
                                }}
                              />
                            );
                          })
                        ) : (
                          <Typography variant="body2" sx={{ color: "#94a3b8", fontStyle: "italic", px: 1 }}>
                            El documento no trataba este momento.
                          </Typography>
                        )}
                      </Box>

                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          p: 1.5,
                          bgcolor: "#fafaff",
                          borderLeft: { lg: "1px solid #f1f5f9" },
                          borderTop: { xs: "1px solid #f1f5f9", lg: "none" },
                        }}
                      >
                        <EncabezadoColumna
                          icono={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                          titulo="Lo que agregó el sistema"
                          color="#4338ca"
                        />
                        {momento.sistema?.length ? (
                          momento.sistema.map((item, i) => (
                            <Box key={i} sx={{ display: "flex", gap: 1, px: 1, py: 0.75 }}>
                              <Box sx={{ mt: "7px", width: 6, height: 6, borderRadius: "50%", bgcolor: "#6366f1", flexShrink: 0 }} />
                              <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                {item}
                              </Typography>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" sx={{ color: "#94a3b8", fontStyle: "italic", px: 1 }}>
                            Sin aportes adicionales.
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </>
          )}
        </Box>

        {/* Documento original (sigue montado en móvil para no recargar el PDF) */}
        <Box sx={{ flex: 1, minWidth: 0, display: esMovil && vista !== "documento" ? "none" : "block" }}>
          <VisorPdf
            pdfBlob={pdfBlob}
            nombre={datos.archivo_original || datos.archivo || "Documento del docente"}
            resaltado={resaltado}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ComparativaDocentePanel;
